import { motion } from 'framer-motion';
import { rgba } from 'polished';
import styled, { keyframes } from 'styled-components';

import { THEMES } from 'utils/config';
import { setTypography, setVh } from 'utils/styles/mixins';
import {
  mediaDesktop,
  mediaDesktopLarge,
  mediaMobileSmallHeight,
  mediaTablet,
} from 'utils/styles/responsive';
import { colors } from 'utils/styles/theme';

const rotation = keyframes`
  from {
    transform: rotate(0deg) scale(0.9);
  }
  to {
    transform: rotate(359deg) scale(0.9);
  }
`;

const swipeX = keyframes`
	0% {
		transform: translateX(0px);
	}
	25% {
		transform: translateX(50rem) rotateZ(1deg);
	}
	50% {
		transform: translateX(0px);
	}
	75% {
		transform: translateX(-50rem) rotateZ(-1deg);
	}
`;

export const Container = styled(motion.div)`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 0;
  height: ${setVh(100)};
  width: 100vw;
  overflow: hidden;

  &:before {
    position: absolute;
    content: '';
    top: 0;
    left: 0;
    right: 0;
    height: 495rem;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.8) 0%,
      rgba(0, 0, 0, 0) 100%
    );
    z-index: 1;
    pointer-events: none;
  }

  &:after {
    position: absolute;
    content: '';
    bottom: 0;
    left: 0;
    right: 0;
    height: 495rem;
    background: linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.8) 0%,
      rgba(0, 0, 0, 0) 100%
    );
    z-index: 1;
    pointer-events: none;
  }
`;

export const Wrapper = styled(motion.div)`
  height: 101%;
  width: 100%;
  position: relative;
  display: flex;

  & > div {
    overflow-x: scroll;
    overflow-y: hidden;
    scroll-behavior: smooth;
    scrollbar-width: none;
    -ms-overflow-style: none;
    display: inline-block;

    &::-webkit-scrollbar {
      opacity: 0;
      width: 0;
      height: 0;
    }
  }

  ${mediaDesktopLarge(`
    & > div {
      overflow-x: hidden;
      overflow-y: auto;
    }
  `)}
`;

export const MapContent = styled(motion.div)<{ height: number }>`
  position: relative;
  margin: auto;
  transition-property: all;
  transition-duration: 0.6s;
  display: inline-block;

  & > img:first-of-type {
    min-width: 100vw;
    min-height: 100vh;
  }

  ${({ height }) => `
    & > img:first-of-type {
      height: auto;
      width: ${height * 1.8}px;
    }
  `}
`;

export const MapImage = styled(motion.img)``;

export const MarkersWrapper = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;

  & > div {
    width: 100%;
    height: 100%;
  }
`;

export const Marker = styled(motion.div)<{ x: string; y: string }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  left: ${({ x }) => x};
  top: ${({ y }) => y};
  width: 20rem;
  height: 20rem;
  background: ${colors.white};
  border-radius: 100%;
`;

export const Line = styled(motion.div)<{ theme: string; lineWidth: number }>`
  position: absolute;
  width: 100rem;
  bottom: 10rem;
  left: 10rem;
  transform-origin: left bottom;
  transform-origin: bottom left;
  height: 1px;
  background: ${colors.white};
  width: ${({ lineWidth }) => (lineWidth ? `${lineWidth}rem` : '0rem')};
  transform: rotate(45deg);

  ${({ theme, lineWidth }) => {
    switch (theme) {
      case THEMES.FOOD:
        return `
          transform: rotate(90deg);
          width: ${lineWidth * 2.8}rem;
          ${mediaTablet`
            transform: rotate(40deg);
          `}
        `;
      case THEMES.HOUSING:
        return `
          transform: rotate(130deg);
          width: ${lineWidth / 1.5}rem;
          ${mediaTablet`
            transform: rotate(62deg);
          `}
        `;
      case THEMES.FACTORIES:
        return `
          transform: rotate(345deg);
          width: ${lineWidth / 1.5}rem;
          ${mediaTablet`
            transform: rotate(19deg);
          `}
        `;
      case THEMES.ROADS:
        return `
          transform: rotate(20deg);
          ${mediaTablet`
            transform: rotate(45deg);
          `}
        `;
      case THEMES.WATER:
        return `
          transform: rotate(205deg);
          width: ${lineWidth / 1.5}rem;
          ${mediaTablet`
            transform: rotate(18deg);
          `}
        `;
      case THEMES.MILITARY:
        return `
          transform: rotate(348deg);
          ${mediaTablet`
            transform: rotate(12deg);
          `}
        `;
    }
  }}

  ${({ theme, lineWidth }) => `
    ${mediaTablet(`
      width: ${lineWidth / 1.5}rem;
    `)}

    ${mediaDesktop(`
      width: ${lineWidth}rem;
    `)}
    ${
      theme === THEMES.WATER
        ? mediaDesktop(`
    width: ${lineWidth / 2.5}rem;
  `)
        : ''
    }
  `};
`;

export const Circle = styled(motion.button)`
  width: 320rem;
  height: 320rem;
  border-radius: 100%;
  border: solid 1px ${colors.white};
  transform: translate(100%, -50%);
  right: 0;
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;

  ${mediaTablet`
    width: 180rem;
    height: 180rem;
  `}

  ${mediaDesktop`
    width: 280rem;
    height: 280rem;
  `}
`;

export const InnerCircle = styled(motion.div)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 0;
  user-select: none;
  animation: ${rotation} 4s ease-in-out 1;
  width: 310rem;
  height: 310rem;
  transform: scale(0.9);

  svg {
    width: 100%;
    height: 100%;
  }

  ${mediaTablet`
    width: 180rem;
    height: 180rem;
  `}

  ${mediaDesktop`
    width: 280rem;
    height: 280rem;
  `}
`;

export const TitleWrapper = styled(motion.div)<{ theme: string }>`
  position: absolute;
  right: 0;
  top: 0;
  left: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  ${({ theme }) => {
    switch (theme) {
      case THEMES.FOOD:
        return `
          transform: rotate(-90deg) translate(0%, 70%);
          ${mediaTablet`
            transform: rotate(-40deg) translateX(110%);
          `}
        `;
      case THEMES.HOUSING:
        return `
          transform: rotate(-130deg) translate(0%, 70%);
          ${mediaTablet`
            transform: rotate(-62deg) translateX(110%);
          `}
        `;
      case THEMES.FACTORIES:
        return `
          transform: rotate(-345deg) translate(0%, 70%);
          ${mediaTablet`
            transform: rotate(-19deg) translateX(110%);
          `}
        `;
      case THEMES.ROADS:
        return `
          transform: rotate(-20deg) translate(0%, 70%);
          ${mediaTablet`
            transform: rotate(-45deg) translateX(110%);
          `}
        `;
      case THEMES.WATER:
        return `
          transform: rotate(-205deg) translate(0%, 70%);
          ${mediaTablet`
            transform: rotate(-18deg) translateX(110%);
          `}
        `;
      case THEMES.MILITARY:
        return `
          transform: rotate(-348deg) translate(0%, 70%);
          ${mediaTablet`
            transform: rotate(-12deg) translateX(110%);
          `}
        `;
    }
  }}

  span {
    color: ${colors.white};
    ${({ theme: { locale } }) => setTypography('mapMarkerLabel', locale)}
    user-select: none;
    background-color: ${rgba(colors.black, 0.4)};
    border-radius: 60rem;
    padding: 20rem 30rem;
  }

  ${mediaTablet`
    align-items: flex-start;
  `}
`;

export const IconWrapper = styled(motion.div)<{
  theme: string;
  active: boolean;
}>`
  width: 185rem;
  height: 185rem;
  border-radius: 100%;
  background: ${({ active }) => (active ? colors.white : colors.shadowGreen)};
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition-property: all;
  transition-duration: 0.6s;
  position: relative;
  z-index: 2;

  & > div {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 85rem;
  }

  svg {
    color: ${colors.taupe};
    width: 100%;
    height: 100%;
  }

  ${mediaTablet`
    width: 130rem;
    height: 130rem;

    & > div {
      height: 55rem;
    }
  `}

  ${mediaDesktop`
    width: 185rem;
    height: 185rem;

    & > div {
      height: 85rem;
    }
  `}

  ${({ theme }) => {
    switch (theme) {
      case THEMES.FOOD:
        return `
          transform: rotate(-90deg);
          ${mediaTablet`
            transform: rotate(-40deg);
          `}
        `;
      case THEMES.HOUSING:
        return `
          transform: rotate(-130deg);
          ${mediaTablet`
            transform: rotate(-62deg);
          `}
        `;
      case THEMES.FACTORIES:
        return `
          transform: rotate(-345deg);
          ${mediaTablet`
            transform: rotate(-19deg);
          `}
        `;
      case THEMES.ROADS:
        return `
          transform: rotate(-20deg);
          ${mediaTablet`
            transform: rotate(-45deg);
          `}
        `;
      case THEMES.WATER:
        return `
          transform: rotate(-205deg);
          ${mediaTablet`
            transform: rotate(-18deg);
          `}
        `;
      case THEMES.MILITARY:
        return `
          transform: rotate(-348deg);
          & > div {
            height: 70rem;
          }
          ${mediaTablet`
            transform: rotate(-12deg);
            & > div {
              height: 55rem;
            }
          `}
        `;
    }
  }}
`;

export const RangeSliderContainer = styled(motion.div)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 2;
  padding: 62rem 40rem;
  pointer-events: none;

  ${mediaTablet`
    display: none !important;
  `}
`;

export const SliderWrapper = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  transform: scale(-1);
`;

export const Slider = styled(motion.div)`
  width: 100%;
  height: 4px;
  background: ${colors.black};
  border-radius: 2px;
  position: relative;
`;

export const Knob = styled(motion.div)`
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  position: absolute;
  height: 10px;
  width: 223rem;
  border-radius: 5px;
  margin-top: -3px;
  background: ${colors.white};
  transition-property: all;
  transition-duration: 0.1s;
`;

export const Mask = styled(motion.div)`
  position: absolute;
  top: 0;
  left 0;
  width: 960rem;
  height: 150vh;
  background: linear-gradient(90deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 100%);
  transform: rotate(-20deg) translate(-22%, -20%);
  pointer-events: none;
  user-select: none;
`;

export const Overlay = styled(motion.div)<{ theme: string }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
  pointer-events: none;
  user-select: none;

  & > div {
    width: 100%;
    height: 100%;

    .lf-player-container {
      width: 100%;
      height: 100%;

      #lottie {
        width: 100%;
        height: 100%;

        svg {
          transform: none !important;
        }
      }
    }
  }

  ${({ theme }) => {
    switch (theme) {
      case THEMES.FOOD:
        return `
          top: -20rem;
          ${mediaTablet`
            top: -10rem;
          `}
          ${mediaDesktop`
            top: -20rem;
          `}
        `;
      case THEMES.HOUSING:
        return `
          top: -20rem;
          ${mediaTablet`
            top: -10rem;
          `}
          ${mediaDesktop`
            top: -25rem;
          `}
        `;
      case THEMES.FACTORIES:
        return `
          top: -10rem;
          ${mediaTablet`
            top: 0rem;
          `}
          ${mediaDesktop`
            top: -10rem;
          `}
          ${mediaMobileSmallHeight(`
            top: -5rem;
          `)}
        `;
      case THEMES.ROADS:
        return `
          top: -5rem;
          ${mediaTablet`
            top: -7rem;
            left: 0;
          `}
        `;
      case THEMES.WATER:
        return `
          top: -10rem;
          left: 10rem;
          ${mediaDesktop`
            top: -10rem;
            left: 10rem;
          `}
        `;
      case THEMES.MILITARY:
        return `
          top: 0rem;
          left: 0rem;
        `;
    }
  }}
`;

export const MapLabel = styled(motion.div)`
  position: absolute;
  left: 36rem;
  top: 110rem;
  z-index: 5;
  user-select: none;

  &:before {
    content: '';
    position: absolute;
    top: -60rem;
    left: 0;
    height: 2px;
    width: 236rem;
    background: ${colors.white};
  }

  h1 {
    color: ${colors.white};
    white-space: pre-wrap;
    ${({ theme: { locale } }) => setTypography('mapLabel', locale)}
  }

  ${mediaTablet`
    left: 32rem;
    bottom: 100rem;
    top: auto;
  `}

  ${mediaDesktop`
    left: 96rem;
    bottom: 160rem;
  `}
`;

export const Hint = styled(motion.div)`
  position: fixed;
  left: 0;
  right: 0;
  z-index: 2;
  bottom: 180rem;
  margin: auto;
  padding: 10rem 30rem;
  width: 540rem;
  height: 115rem;
  border-radius: 115rem;
  display: none;
  flex-direction: row;
  gap: 30rem;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.8);
  pointer-events: none;
  user-select: none;

  svg {
    width: 70rem;
  }

  p {
    color: ${colors.white};
    ${({ theme: { locale } }) => setTypography('mapHint', locale)}
  }

  ${mediaDesktopLarge`
    display: flex;
  `}
`;

export const MobileHint = styled(motion.div)`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 130rem;
  margin: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 30rem;
  z-index: 2;
  pointer-events: none;
  user-select: none;

  svg {
    animation-delay: 3s;
    animation: ${swipeX} 6s linear infinite;
    width: 225rem;
  }

  p {
    color: ${colors.white};
    ${({ theme: { locale } }) => setTypography('mapHint', locale)}
  }

  ${mediaDesktopLarge`
    display: none;
  `}
`;

export const RangeSliderLabels = styled(motion.div)`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 80rem;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 62rem 40rem;
`;

export const Label = styled(motion.div)`
  display: flex;
  flex-direction: row;
  gap: 10rem;
  align-items: center;

  span {
    color: ${colors.white};
    ${({ theme: { locale } }) => setTypography('mapHint', locale)}
  }
`;

export const NorthArrow = styled(motion.div)`
  position: absolute;
  top: 250rem;
  right: 120rem;
  user-select: none;
  pointer-events: none;
  z-index: 1;
  display: none;

  svg {
    height: 180rem;
  }

  ${mediaTablet`
    display: block;
  `}

  ${mediaDesktop`
    display: block;
    top: 300rem;
    right: 180rem;

    svg {
      height: 230rem;
    }
  `}
`;
